// The reads behind Reporting's Associated Entities charts (`entityCharts.ts`):
// `GET /analytics/spend-by-vehicle-location`, `GET /analytics/spend-by-vehicle`
// and `GET /analytics/tag-exceptions` under the Filters' criteria
// (`docs/api.md` §16.11–§16.13), re-read when they or the refresh change.
import { useEffect, useState } from 'react'
import { errorMessage } from '../api/errors'
import type { AnalyticsParams } from '../api/types'
import { loadAnalyticsSpendByVehicle, loadAnalyticsSpendByVehicleLocation, loadAnalyticsTagExceptions } from './data'
import type { EntityChartData } from './entityCharts'

/**
 * The Associated Entities reads for one set of filters. A failed read is
 * `null`, so only its chart says so; `error` names the first failure.
 */
async function readEntityChartData(params: AnalyticsParams): Promise<{ data: EntityChartData; error: string | null }> {
  const [vehicleLocations, vehicles, tagExceptions] = await Promise.allSettled([
    loadAnalyticsSpendByVehicleLocation(params),
    loadAnalyticsSpendByVehicle(params),
    loadAnalyticsTagExceptions(params),
  ])
  const settled = <T,>(result: PromiseSettledResult<T>): T | null => (result.status === 'fulfilled' ? result.value : null)
  const failed = [vehicleLocations, vehicles, tagExceptions].find((r) => r.status === 'rejected')
  return {
    data: { vehicleLocations: settled(vehicleLocations), vehicles: settled(vehicles), tagExceptions: settled(tagExceptions) },
    error: failed === undefined ? null : errorMessage(failed.reason),
  }
}

/**
 * The Associated Entities data for one set of filters, as `useChartData` in
 * `ReportingScreen.tsx` reads the other charts'. `null` params read nothing.
 */
export function useEntityChartData(
  params: AnalyticsParams | null,
  refreshNonce: number,
): { data: EntityChartData | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<EntityChartData | null>(null)
  const [loading, setLoading] = useState(params !== null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (params === null) {
      setData(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void readEntityChartData(params).then((result) => {
      if (cancelled) return
      setData(result.data)
      setError(result.error)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [params, refreshNonce])
  return { data, loading, error }
}
